// Utility types and helpers for working with Vue Router in a type-safe and maintainable way.
import type {
  RouteLocationAsPathGeneric,
  RouteLocationAsRelativeGeneric,
  RouteLocationRaw,
  Router,
  RouteRecordNameGeneric,
  RouteRecordRaw,
} from 'vue-router'

import listRouteMap from '@/constants/listRouteMap'
import { organizationDoctypes } from '@/constants/relations'
import { routeMap } from '@/constants/routeMap'
import { getOrganizationDetailRoute } from '@/routers/routes/mainRoutes/routeRelationships/routeOrganizationDetail'

// --- Redirect Route Types ---
// These types help enforce the use of named routes (preferred) over path-based navigation.

// RouteLocationRaw is a union of all valid route location types (string, object, etc.)
type RedirectRouteUnionTypeString = RouteLocationRaw

// Helper type: Remove 'string' from a union type (so we can distinguish between string and object route definitions)
type OmitString<T> = T extends string ? never : T

// Only allow non-string route location types (i.e., object-based route definitions)
type RedirectRoutePathOrRelative = OmitString<RedirectRouteUnionTypeString>

// Enforce that if you use 'path', you cannot use 'name' or 'params' (and vice versa)
interface RedirectCustomRoutePath extends RouteLocationAsPathGeneric {
  /**
   * @deprecated Do not use 'name' or 'params' when using 'path'.
   */
  name?: never
  params?: never
}
interface RedirectCustomRouteRelative extends Omit<RouteLocationAsRelativeGeneric, 'path'> {
  /**
   * @deprecated Do not use 'path' when using 'name' or 'params'.
   */
  path?: never
}

// Map union types to the correct custom type
// If the type is a path-based route, use RedirectCustomRoutePath
// If the type is a name-based route, use RedirectCustomRouteRelative
type RedirectExchangeRoutePath<T> = T extends RouteLocationAsPathGeneric
  ? RedirectCustomRoutePath
  : never
// If the type is a relative (named) route, use RedirectCustomRouteRelative
type RedirectExchangeRouteRelative<T> = T extends RouteLocationAsRelativeGeneric
  ? RedirectCustomRouteRelative
  : never

type RedirectRoutePath = RedirectExchangeRoutePath<RedirectRoutePathOrRelative>
type RedirectRouteRelative = RedirectExchangeRouteRelative<RedirectRoutePathOrRelative>

// The main type for redirecting: either a path-based or a name-based route object
// (Prefer using RedirectRouteRelative for named routes)
type RedirectRoute = RedirectRoutePath | RedirectRouteRelative

/**
 * Programmatically redirect using a route object or string.
 * Prefer using named route objects for maintainability.
 * Handles navigation errors and logs useful debug info.
 */
const routerRedirect = async (
  routerInstance: Router,
  redirectRoute: RedirectRoute | RedirectRouteUnionTypeString,
): Promise<void> => {
  // Debug output for the type of route being used
  if (typeof redirectRoute === 'string') {
    console.debug('routeString', redirectRoute)
  } else if (Object.hasOwn(redirectRoute, 'name')) {
    // Named route (recommended)
    console.debug('route (named)', redirectRoute)
  } else if (Object.hasOwn(redirectRoute, 'path')) {
    // Path-based route (not recommended)
    console.debug('route (path)', redirectRoute)
  }

  await routerInstance.push(redirectRoute)
}

const routerRedirectBackRoute = async (
  routerInstance: Router,
  redirectKey: string,
): Promise<void> => {
  const localStorageRedirect = localStorage.getItem(redirectKey)

  if (!localStorageRedirect) {
    console.warn('[Redirect] No redirect route found in Local Storage for key:', redirectKey)
    return
  }

  // Clean up the localStorage entry after retrieving it
  localStorage.removeItem(redirectKey)

  await routerInstance.push(JSON.parse(localStorageRedirect))

  // If the new route does not have a redirectBackRoute query param, clean up old backRoute_ entries
  if (!routerInstance.currentRoute.value.query['redirectBackRoute']) {
    Object.keys(localStorage).forEach((currentKey) => {
      if (currentKey.startsWith('backRoute_')) {
        localStorage.removeItem(currentKey)
      }
    })
  }
}

/**
 * Redirect to a named route, persisting the current route as back route
 * (consumed by `routerRedirectBackRoute`).
 */
const routerRedirectWithBackRoute = async (
  routerInstance: Router,
  routeName: RouteRecordNameGeneric,
): Promise<void> => {
  const currentRoute = routerInstance.currentRoute.value
  const redirectBackRouteKey = `backRoute_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`

  localStorage.setItem(
    redirectBackRouteKey,
    JSON.stringify({
      name: currentRoute.name,
      params: currentRoute.params,
      query: currentRoute.query,
    }),
  )

  await routerInstance.push({
    name: routeName,
    query: {
      redirectBackRoute: redirectBackRouteKey,
    },
  })
}

/**
 * Ensures a route path starts with '/'.
 * Useful for normalizing route paths.
 */
const ensureRootRoutePath = (routePath: string): string => {
  let currentRoutePath = routePath
  const rootRoutePrefix = '/'

  if (!currentRoutePath.startsWith(rootRoutePrefix)) {
    currentRoutePath = rootRoutePrefix.concat(currentRoutePath)
  }

  return currentRoutePath
}

/**
 * Removes all 'meta' properties from a list of route records.
 * Useful for stripping auth or other metadata from routes.
 */
const ensureNoAuthRoutes = (routeList: RouteRecordRaw[]): RouteRecordRaw[] => {
  routeList.forEach((currentRoute) => {
    delete currentRoute.meta
  })

  return routeList
}

/**
 * Get the route name based on targetDoctype and target.
 */
const getRouteName = (targetDoctype: string, targetViewtype: string): RouteRecordNameGeneric => {
  if (targetViewtype === 'List' || targetViewtype === 'Kanban') {
    return listRouteMap[targetDoctype] ?? ''
  } else if (targetViewtype === 'Detail') {
    // Normalize the targetDoctype to match keys in routeMap
    const parsedTargetDoctype = targetDoctype.toLowerCase().replace(/ /gu, '-')

    if (organizationDoctypes.includes(parsedTargetDoctype)) {
      return getOrganizationDetailRoute(parsedTargetDoctype).name
    }

    return routeMap[parsedTargetDoctype] ?? ''
  }

  return ''
}

export {
  ensureNoAuthRoutes,
  ensureRootRoutePath,
  getRouteName,
  type RedirectRoute,
  type RedirectRoutePath,
  type RedirectRouteRelative,
  routerRedirect,
  routerRedirectBackRoute,
  routerRedirectWithBackRoute,
}
